/*
 * Class for executing rearrange changes.
 *
 * A rearrange change is defined as moving a set of elements specified by a selector
 * to another location in the DOM specified by another selector. The reinsertion of
 * the element can be performed as a prepend, append, before, or after insertion.
 *
 * @module
 */

var _ = require('optimizely-client-fn');

var constants = require('core/action/constants');
var dataHelpers = require('core/action/element_data');
var deferred = require('core/utils/deferred');
var DirectiveStore = require('core/di').get('stores/directive');
var enums = require('core/action/enums');
var PromiseEnv = require('core/lib/promise').Promise;
var timingHelpers = require('core/action/timing_helpers');
var DocumentEnv = require('core/env/document');

var api = require('api/utils').create();

/**
 * @constructor
 *
 * @param {Change} change
 * @param {ChangeConfig} config
 */
function RearrangeChange(change:Change, config:ChangeConfig) {
  this.change = change;
  this.identifier = config.identifier;
  this.startTime = config.startTime;
}

/**
 * Apply the change.
 *
 * Ensure that both selectors necessary for the rearrange operation are
 * present in the DOM before executing the change.
 *
 * If either selector fails to find an element, reject the change deferred.
 *
 * @return {Deferred}
 */
RearrangeChange.prototype.apply = function (): Deferred {
  this.applyDeferred = deferred();
  // If we have an invalid selector, document.querySelector and the api will throw an error
  try {
    var selectorElement = DocumentEnv.querySelector(this.change.selector);
    var insertSelectorElement = DocumentEnv.querySelector(this.change.insertSelector);

    // If both necessary elements exist now, apply the change immediately.
    if (selectorElement && insertSelectorElement) {
      this.maybeApplyToElement();
    } else {
      // Wait for both the element selector and the insert selector before applying the change.
      var selectorDef = deferred();
      var insertSelectorDef = deferred();

      var onTimeoutFn = _.partial(this.applyDeferred.reject, new Error('Unable to find source element'));
      var onInsertTimeoutFn = _.partial(this.applyDeferred.reject, new Error('Unable to find destination element'));
      var observeSelectorOptions = { once: true};
      var observeInsertSelectorOptions = { once: true};
      if (DirectiveStore.shouldObserveChangesUntilTimeout()) {
        observeSelectorOptions = _.assign(observeSelectorOptions, {
          timeout: _.partial(timingHelpers.isTimedOut, this.startTime),
          onTimeout: onTimeoutFn
        });
        observeInsertSelectorOptions = _.assign(observeInsertSelectorOptions, {
          timeout: _.partial(timingHelpers.isTimedOut, this.startTime),
          onTimeout: onInsertTimeoutFn
        });
      } else if (DirectiveStore.isEditor()) {
        // Continue to observe indefinitely and apply indefinitely, but ensure the appliedDeferred promise times out.
        // Else we'll freeze the editor if the source or destination element can't be found. (WEB-2512)
        api.waitUntil(_.partial(timingHelpers.isTimedOut, this.startTime))
          .then(onTimeoutFn, onTimeoutFn);
        api.waitUntil(_.partial(timingHelpers.isTimedOut, this.startTime))
          .then(onInsertTimeoutFn, onInsertTimeoutFn);
      } else {
        // Continue to observe indefinitely and apply indefinitely.
        // The appliedDeferred will remain pending (and therefore will block any dependent changes)
        // until its first application.
      }

      var unobserveSelector = api.observeSelector(
        this.change.selector,
        selectorDef.resolve,
        observeSelectorOptions
      );

      var unobserveInsertSelector = api.observeSelector(
        this.change.insertSelector,
        insertSelectorDef.resolve,
        observeInsertSelectorOptions
      );

      this.unobserveSelector = function() {
        unobserveSelector();
        unobserveInsertSelector();
      };

      PromiseEnv.all([
        selectorDef,
        insertSelectorDef,
      ]).then(_.bind(this.maybeApplyToElement, this), this.applyDeferred.reject);
    }
  } catch(error) {
    this.applyDeferred.reject(error);
  }

  return this.applyDeferred;
};

/**
 * Apply the change
 *
 * A rearrange change consists of moving a target element identified by change.selector
 * to another place in the DOM relative to the insertion target element identified by
 * change.insertSelector.
 *
 * The method of insertion is specified by change.operator, which can be one of the following:
 *   - APPEND   : Insert the detached element as the last child of the insertion element
 *   - PREPEND  : Insert the detached element as the first child of the insertion element
 *   - BEFORE   : Insert the detached element as the previous sibling of the insertion element
 *   - AFTER    : Insert the detached element as the next sibling of the insertion element
 *
 * When invoked, both selectors necessary to perform the operation must be present in the DOM.
 *
 * In order to be able to undo this operation, the target element's previous sibling OR parent
 * must be stored so the undo operation knows which element and operation to use to undo the rearrange.
 *
 */
RearrangeChange.prototype.maybeApplyToElement = function () {
  var elementsToMove = DocumentEnv.querySelectorAll(this.change.selector);
  _.each(elementsToMove, _.bind(function (elementToMove) {
    // TODO
    if (dataHelpers.getData(elementToMove, this.change.id, this.identifier)) {
      return;
    }

    // All elements must have either a sibling before OR be the first child of a parent element.
    // Store this info on the element for help in undoing the rearrange.
    var undoData:any = {
      undoElement: elementToMove.previousSibling,
      undoType: enums.DOMInsertionType.AFTER,
    };
    if (elementToMove.parentNode.firstChild === elementToMove) {
      undoData.undoElement = elementToMove.parentNode;
      undoData.undoType = enums.DOMInsertionType.PREPEND;
    }

    var detachedElement = elementToMove.parentNode.removeChild(elementToMove);
    var elementForInsertion = DocumentEnv.querySelector(this.change.insertSelector);
    var parentOfInsertionElement = elementForInsertion.parentNode;

    undoData.element = detachedElement;

    switch (this.change.operator) {
      // Before the element as a sibling
      case enums.DOMInsertionType.BEFORE:
        parentOfInsertionElement.insertBefore(detachedElement, elementForInsertion);
        break;
      // After the element as a sibling
      case enums.DOMInsertionType.AFTER:
        parentOfInsertionElement.insertBefore(detachedElement, elementForInsertion.nextSibling);
        break;
      // As the first child of the element
      case enums.DOMInsertionType.PREPEND:
        var firstChild = elementForInsertion.firstChild;
        elementForInsertion.insertBefore(detachedElement, firstChild);
        break;
      // As the last child of the element
      case enums.DOMInsertionType.APPEND:
        elementForInsertion.appendChild(detachedElement);
        break;
      default:
        break;
    }

    // store change id for undo later (if needed)
    detachedElement.setAttribute(constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id, '');

    // Store a reference to the node so that we can remove it later if we want
    dataHelpers.setData(detachedElement, this.change.id, this.identifier, undoData);
  }, this));
  this.applyDeferred.resolve();
};

/**
 * If this change is still being observed, cancel any future
 * invocations of it by invoking its unobserveSelector function.
 */
RearrangeChange.prototype.cancel = function () {
  if (this.unobserveSelector) {
    this.unobserveSelector();
  }
};

if (__UNDO_CHANGES_UPON_VIEW_DEACTIVATION__ || __TEST__) {
  /**
   * Undo action
   *
   * Using the undoData stored on the target element when the rearrange was performed, undo the rearrange
   * by retrieving the previous sibling OR parent element from the undo data (undoData.undoElement)
   * and move the target element back to its original location relative to that undoElement based
   * on the operator provided (undoData.operator).
   *
   */
  RearrangeChange.prototype.undo = function () {
    var elementList = DocumentEnv.querySelectorAll('[' + constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id + ']');
    _.each(elementList, _.bind(function (element) {
      var undoData = dataHelpers.getData(element, this.change.id, this.identifier);

      // Remove node from the DOM
      var detachedElement = undoData.element.parentNode.removeChild(undoData.element);
      var elementForInsertion = undoData.undoElement;
      var parentOfInsertionElement = elementForInsertion.parentNode;

      switch (undoData.undoType) {
        // After the element as a sibling
        case enums.DOMInsertionType.AFTER:
          parentOfInsertionElement.insertBefore(detachedElement, elementForInsertion.nextSibling);
          break;
        // As the first child of the element
        case enums.DOMInsertionType.PREPEND:
          var firstChild = elementForInsertion.firstChild;
          elementForInsertion.insertBefore(detachedElement, firstChild);
          break;
        default:
          break;
      }

      element.removeAttribute(constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id);
      dataHelpers.removeData(element, this.change.id, this.identifier);
    }, this));
    return deferred().resolve(enums.changeState.UNAPPLIED);
  };
}

/**
 * PluginManager registration method
 * @param {PluginManager} PluginManager
 */
module.exports = function(PluginManager:PluginManagerClass) {
  PluginManager.registerChangeApplier(enums.changeType.REARRANGE, RearrangeChange);
};

if (__TEST__) {
  module.exports.plugin = RearrangeChange;
}
